import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import AuthLayout from '@/components/AuthLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowRight } from 'lucide-react';

export default function Login() {
  const { login, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);

  const handleGoogleSignIn = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(form.email, form.password);
      
      if (user.role === 'admin') {
        logout();
        toast.error('Administrators must log in at /admin/login');
        navigate('/admin/login');
        return;
      }
      
      toast.success('Welcome back!');
      
      if (user.role === 'member') navigate('/dashboard');
      else if (!user.profile_completed) navigate('/profile/complete');
      else if (user.membership_status === 'none') navigate('/membership/apply');
      else navigate('/membership/status');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout heading="Welcome back" subtitle="Sign in to access your member portal">
      <div className="p-8" style={{ backgroundColor: '#FFFFFF', border: '1px solid #000' }}>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <Label htmlFor="email" className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-muted)' }}>Email address</Label>
            <Input
              id="email" type="email" required placeholder="you@example.com"
              value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              className="h-11 rounded-none" style={{ borderColor: 'var(--border-clr)', backgroundColor: '#FDFCFA' }}
              data-testid="login-email-input"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label htmlFor="password" className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>Password</Label>
              <Link to="/forgot-password" className="text-xs" style={{ color: 'var(--text-muted)', textDecoration: 'underline', textUnderlineOffset: '3px' }} data-testid="forgot-password-link">
                Forgot password?
              </Link>
            </div>
            <div className="relative">
              <Input
                id="password" type={showPass ? 'text' : 'password'} required placeholder="••••••••"
                value={form.password}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                className="h-11 rounded-none" style={{ borderColor: 'var(--border-clr)', backgroundColor: '#FDFCFA', paddingRight: '2.75rem' }}
                data-testid="login-password-input"
              />
              <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 transition-opacity hover:opacity-70" style={{ color: 'var(--text-muted)' }}>
                {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <Button
            type="submit" className="w-full h-12 flex items-center justify-center gap-2 mt-2 uppercase tracking-wider font-semibold" disabled={loading}
            data-testid="login-form-submit-button"
            style={{ backgroundColor: '#000', color: 'white', borderRadius: 0, fontSize: '0.8rem' }}
            onMouseEnter={(e) => { if (!loading) { e.currentTarget.style.backgroundColor = '#fff'; e.currentTarget.style.color = '#000'; e.currentTarget.style.boxShadow = 'inset 0 0 0 1px #000'; } }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#000'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.boxShadow = 'none'; }}
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                  <path d="M12 2a10 10 0 0 1 10 10" />
                </svg>
                Signing in…
              </span>
            ) : (<><span>Sign in</span><ArrowRight size={15} /></>)}
          </Button>
        </form>

        {/* Divider */}
        <div className="flex items-center gap-3 my-5">
          <div style={{ flex: 1, height: '1px', backgroundColor: '#E5E5E5' }} />
          <span style={{ fontSize: '0.65rem', color: '#999', letterSpacing: '0.12em', textTransform: 'uppercase' }}>or</span>
          <div style={{ flex: 1, height: '1px', backgroundColor: '#E5E5E5' }} />
        </div>

        {/* Google Sign-In */}
        <button onClick={handleGoogleSignIn}
          className="w-full h-11 flex items-center justify-center gap-3 text-sm font-medium transition-colors"
          style={{ border: '1px solid #E5E5E5', backgroundColor: '#fff', color: '#000' }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#F9F9F9'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fff'; }}
          data-testid="google-login-button">
          <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
            <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z"/>
            <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z"/>
            <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332Z"/>
            <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58Z"/>
          </svg>
          Continue with Google
        </button>
      </div>

      <div className="mt-6 text-center">
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
          Not a member?{' '}
          <Link to="/register" style={{ color: '#000', fontWeight: 600 }}>Apply for access</Link>
        </p>
      </div>
      <div className="mt-8 flex items-center gap-3 justify-center">
        <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--border-clr)' }} />
        <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>Messums Art Market</span>
        <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--border-clr)' }} />
      </div>
    </AuthLayout>
  );
}
